import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Radar, ShieldAlert } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { LoadingBlock } from "../components/Spinner";
import { EntityTagsRow, EntityTagFilters, useEntityTagData, useTagFilter, matchesEntityTagFilter, entityRef, KIND_HREFS } from "../components/EntityTags";
import { RelatedRecords, type RelatedLink } from "../components/RelatedRecords";
import { NextSteps, type NextStepItem } from "../components/NextSteps";
import { RegisterToolbar, SummaryTiles } from "../components/RegisterToolbar";
import { downloadCsv, matchesSearch, by, type SortOption, type ExportColumn } from "../lib/registerExport";
import { AiImpactAssessmentPanel, RatingBadge, type ImpactCatalogue } from "../components/AiImpactAssessmentPanel";
import {
  EmptyState, Field, ModalOverlay, ModalHeader, input, textarea, secondaryBtn, dangerBtn, fmtDay,
} from "./resilience/shared";
import { DetailFields, DetailSection, EditButton, RecordActions } from "../components/RecordView";
import { useCurrentUser } from "../auth/useCurrentUser";
import { PlaybookBar } from "../components/PlaybookBar";
import { dataCategoryLabel } from "../lib/dataCategories";
import { DataCategoryPicker } from "../components/DataCategoryPicker";
import { DpiaCheckOutcome, ShadowAiOnboardingPanel } from "../components/ShadowAiOnboarding";
import { useDeepLinkScroll } from "../hooks/useDeepLinkScroll";
import { safeHref } from "../utils/safeHref";
import { fmtDateOnly } from "../lib/dateOnly";

// The AI system register, shadow-AI detection and the regulatory watch.
//
// The risk class is never editable here: it is derived server-side from the AI Act answers, and
// letting a page set it would let an organisation mark a recruitment-screening system "minimal"
// and make the obligations disappear. The questions are editable; the verdict is not.

const RISK: Record<string, { label: string; color: string; bg: string }> = {
  // The Act's classes are a risk ladder, so they use the risk-band tokens — swapped per
  // scheme by ThemeContext, including the Wong colour-blind palette. Never literals.
  prohibited:  { label: "Prohibited",  color: theme.color.riskCritText, bg: theme.color.riskCritBg },
  high:        { label: "High risk",   color: theme.color.riskHighText, bg: theme.color.riskHighBg },
  limited:     { label: "Limited",     color: theme.color.accentText,   bg: theme.color.accentBg },
  minimal:     { label: "Minimal",     color: theme.color.riskLowText,  bg: theme.color.riskLowBg },
  unassessed:  { label: "Unassessed",  color: theme.color.warningText,  bg: theme.color.warningBg },
};

interface AnnexPurpose { id: string; label: string }
interface Catalogue extends Partial<ImpactCatalogue> {
  lifecycleStages: string[];
  hosting: string[];
  /** AI_HOSTING_LABELS from backend lib/aiSystems.ts ("vendor_saas" → "Vendor SaaS"). */
  hostingLabels?: Record<string, string>;
  roles: string[];
  annexIIIPurposes: AnnexPurpose[];
}
/** The approved AI impact assessment standing for a system, as GET /ai-systems summarises it. */
interface ImpactSummary {
  current: { id: string; residualRating: string | null; approvedAt: string | null; approvedByName: string | null; reviewDueAt: string | null; standing: "approved" | "review_due" } | null;
  drafts: number;
}
interface AiSystem {
  id: string; name: string; purpose: string | null; vendor: string | null; hosting: string | null; modelName?: string | null;
  lifecycleStage: string; role: string | null; riskClass: string;
  annexIIIPurposes: string[]; interactsWithPeople: boolean; generatesContent: boolean;
  emotionRecognition: boolean; prohibitedPractice: boolean;
  ownerName: string | null; impactAssessmentId: string | null;
  detectedFrom: string | null; detectionEvidence: string | null; detectionKey: string | null;
  confirmedAt: string | null; reviewedAt: string | null; notes: string | null;
  /** A deployer's assessment of how the model was developed (EDPB Opinion 28/2024); the date is the server's. */
  modelDevelopmentAssessment?: string | null; modelDevelopmentAssessedAt?: string | null;
  obligations: string[];
  supplierId?: string | null; assetId?: string | null; riskAssessedAt?: string | null;
  impactAssessment?: ImpactSummary;
  /** The risk register entries that record this system's risks (ISO 42001 6.1.2). */
  riskIds?: string[];
  risks?: Array<{ id: string; displayId: number | null; title: string; status: string }>;
  // The personal data it handles and the processing record it is held under (GDPR Art. 30).
  dataCategories?: string[];
  ropaEntryId?: string | null;
  ropaEntry?: { id: string; name: string; dataCategories: string[] } | null;
  // Shadow AI onboarding (backend lib/shadowAiOnboarding.ts) and the DPIA check it raised.
  onboardedAt?: string | null;
  dpiaCheckRaisedAt?: string | null;
  dpiaCheckReasons?: string[];
  dpiaCheckResolvedAt?: string | null;
  dpiaCheckResolution?: string | null;
}

const RISK_ORDER = ["prohibited", "high", "limited", "minimal", "unassessed"];
const DAY = 86_400_000;
/** An approved assessment whose review falls within this many days counts as due. */
const ASSESSMENT_DUE_SOON_DAYS = 30;

const unconfirmed = (s: AiSystem) => !s.confirmedAt && !!s.detectedFrom;
// Detection writes a readable source ("Installed software (Intune / Defender)"); rows seeded
// before that carry a bare key, shown readably here.
const DETECTED_FROM_LABELS: Record<string, string> = {
  m365_oauth: "Microsoft 365 app consents",
  intune: "Installed software (Intune / Defender)",
};
const detectedFromLabel = (v: string | null) => (v ? DETECTED_FROM_LABELS[v] ?? v : v);
const live = (s: AiSystem) => s.lifecycleStage !== "retired";
/** A detected tool with no supplier, not yet onboarded: what the one-step onboarding is offered on (the server decides). */
const onboardable = (s: AiSystem) => !!s.detectionKey && !s.supplierId && !s.onboardedAt && live(s);
/** The DPIA check onboarding raised, still open (no DPIA linked, no outcome recorded). */
const dpiaCheckOpen = (s: AiSystem) => !!s.dpiaCheckRaisedAt && !s.dpiaCheckResolvedAt && !s.impactAssessmentId && live(s);
/**
 * Due: a live high-risk system with no approved assessment, or an approved assessment whose review
 * date is past or near. Whether it *is* past is the server's standing; "near" only brings it forward.
 */
function assessmentDue(s: AiSystem): boolean {
  if (!live(s)) return false;
  const current = s.impactAssessment?.current ?? null;
  if (!current) return s.riskClass === "high" || s.riskClass === "prohibited";
  if (current.standing === "review_due") return true;
  return !!current.reviewDueAt && new Date(current.reviewDueAt).getTime() - Date.now() <= ASSESSMENT_DUE_SOON_DAYS * DAY;
}

type AiView = "all" | "prohibited" | "high" | "limited" | "minimal" | "unassessed" | "unconfirmed" | "assessments";

const inAiView = (s: AiSystem, view: AiView) =>
  view === "all" ? true
  : view === "unconfirmed" ? unconfirmed(s)
  : view === "assessments" ? assessmentDue(s)
  : s.riskClass === view;

const AI_SORTS: SortOption<AiSystem>[] = [
  { key: "risk", label: "Risk class", compare: (a, b) => by.rank<AiSystem>((s) => s.riskClass, RISK_ORDER)(a, b) || a.name.localeCompare(b.name) },
  { key: "name", label: "Name", compare: by.text<AiSystem>((s) => s.name) },
  { key: "reviewed", label: "Least recently reviewed", compare: (a, b) => (a.reviewedAt ? new Date(a.reviewedAt).getTime() : 0) - (b.reviewedAt ? new Date(b.reviewedAt).getTime() : 0) },
  { key: "assessment", label: "Assessment review due", compare: by.date<AiSystem>((s) => s.impactAssessment?.current?.reviewDueAt, "asc") },
];
interface LinkOption { id: string; name: string }
interface AiLookups { dpias: LinkOption[]; suppliers: LinkOption[]; assets: LinkOption[]; ropa: LinkOption[]; risks: LinkOption[] }
const riskRef = (r: { displayId?: number | null; title: string }) => `${r.displayId != null ? `RISK-${String(r.displayId).padStart(3, "0")} ` : ""}${r.title}`;
interface Attention { id: string; type: string; title: string; description: string; severity: string }
interface WatchEntry {
  key: string; kind: string; subject: string; currentEdition: string; newEdition: string;
  impact: string; publishedOn: string; summary: string; platformUpdated: boolean;
  url?: string; acknowledged: boolean;
  /** "standards_watch" when the platform owner applied the change (backend lib/regulatoryWatch.ts). */
  origin?: "catalogue" | "standards_watch";
}

const words = (s: string) => s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

function systemLinks(f: { impactAssessmentId: string; supplierId: string; assetId: string; ropaEntryId: string; riskIds: string[] }, lookups: AiLookups | null): RelatedLink[] {
  const name = (list: LinkOption[] | undefined, id: string, fallback: string) => list?.find((o) => o.id === id)?.name ?? fallback;
  return [
    ...(f.ropaEntryId ? [{ kindLabel: "Processing activity", label: name(lookups?.ropa, f.ropaEntryId, "Linked processing activity"), href: KIND_HREFS.ropa(f.ropaEntryId) }] : []),
    ...(f.impactAssessmentId ? [{ kindLabel: "DPIA", label: name(lookups?.dpias, f.impactAssessmentId, "Linked impact assessment"), href: KIND_HREFS.dpia(f.impactAssessmentId) }] : []),
    ...(f.supplierId ? [{ kindLabel: "Supplier", label: name(lookups?.suppliers, f.supplierId, "Linked supplier"), href: KIND_HREFS.supplier(f.supplierId) }] : []),
    ...(f.assetId ? [{ kindLabel: "Asset", label: name(lookups?.assets, f.assetId, "Linked asset"), href: KIND_HREFS.asset(f.assetId) }] : []),
    ...f.riskIds.map((id) => ({ kindLabel: "Risk", label: name(lookups?.risks, id, "Linked risk"), href: KIND_HREFS.risk(id) })),
  ];
}


type Form = {
  name: string; purpose: string; vendor: string; hosting: string; lifecycleStage: string;
  role: string; ownerName: string; annexIIIPurposes: string[];
  interactsWithPeople: boolean; generatesContent: boolean; emotionRecognition: boolean;
  prohibitedPractice: boolean; notes: string; modelDevelopmentAssessment: string;
  /** The classification questions have been answered. Until they are, none is sent: unanswered is unassessed, never minimal. */
  classified: boolean;
  // Links to the records that answer for the system elsewhere.
  impactAssessmentId: string; supplierId: string; assetId: string; ropaEntryId: string;
  dataCategories: string[];
  riskIds: string[];
};

/**
 * What the register entry still needs. A class worked out from unanswered
 * questions is "unassessed", which is listed until someone answers them.
 */
function systemSteps(s: AiSystem, f: Form): NextStepItem[] {
  if (s.lifecycleStage === "retired") return [];
  const high = s.riskClass === "high" || s.riskClass === "prohibited";
  const yearAgo = Date.now() - 365 * 86_400_000;
  const reviewed = !!s.reviewedAt && new Date(s.reviewedAt).getTime() >= yearAgo;
  return [
    { done: !!s.confirmedAt, label: "Confirm the detected system is in use", hint: onboardable(s) ? "Onboard it below: one step adds its vendor as a prospective supplier and raises the DPIA check." : "Editing and saving the entry confirms it." },
    { done: s.riskClass !== "unassessed", label: "Answer the classification questions", hint: "Unanswered is recorded as unassessed, not minimal." },
    ...(s.riskClass === "prohibited" ? [{ done: false, label: "Stop the prohibited practice or retire the system", urgent: true, hint: "EU AI Act Art. 5." }] : []),
    { done: !!f.ownerName.trim(), label: "Name an owner", hint: "ISO 42001 expects someone accountable for each system." },
    { done: !!f.purpose.trim(), label: "Say what it is used for" },
    { done: !high || !!s.impactAssessment?.current, label: "Approve an AI impact assessment", hint: "Below: rate each dimension, then have someone other than its author approve it (ISO 42001 6.1.4).", urgent: high && !s.impactAssessment?.current },
    ...(s.impactAssessment?.current?.standing === "review_due" ? [{ done: false, label: "Review the impact assessment", hint: "It is past its review date. Start a review below.", urgent: true }] : []),
    { done: !high || !!f.impactAssessmentId, label: "Link the data protection impact assessment (DPIA)", hint: "Expected for a high-risk system that processes personal data. An AI impact assessment does not replace it.", urgent: false },
    { done: f.dataCategories.length === 0 || !!f.ropaEntryId, label: "Link the processing record it is held under", hint: "It handles personal data, so the activity it serves belongs on the RoPA (GDPR Art. 30)." },
    { done: !(high || s.annexIIIPurposes.length > 0) || f.riskIds.length > 0, label: "Record its risks in the risk register", hint: "Link the risk register entries that cover it (ISO 42001 6.1.2, AI Act Art. 9).", urgent: high && f.riskIds.length === 0 },
    { done: !f.vendor.trim() || !!f.supplierId, label: "Link the vendor in the supplier register", hint: "So its due diligence and contract cover the system." },
    ...(dpiaCheckOpen(s) ? [{ done: false, label: "Decide whether it needs a DPIA", hint: "Raised when it was onboarded. Link a DPIA, or record why one is not needed below." }] : []),
    { done: reviewed, label: s.reviewedAt ? "Review it again (last reviewed over a year ago)" : "Record a review", hint: "Use Mark reviewed, under Actions, once the entry has been checked." },
  ];
}
const EMPTY: Form = {
  name: "", purpose: "", vendor: "", hosting: "vendor_saas", lifecycleStage: "proposed",
  role: "deployer", ownerName: "", annexIIIPurposes: [],
  interactsWithPeople: false, generatesContent: false, emotionRecognition: false,
  prohibitedPractice: false, notes: "", modelDevelopmentAssessment: "", classified: false,
  impactAssessmentId: "", supplierId: "", assetId: "", ropaEntryId: "", dataCategories: [],
  riskIds: [],
};

export function AiSystemsPage() {
  // Every write on /ai-systems needs risks:write (the API enforces it); without it the page is
  // the read-only view alone.
  const { user } = useCurrentUser();
  const canEdit = !!user?.permissions?.includes("risks:write");
  const [systems, setSystems] = useState<AiSystem[] | null>(null);
  // The system open in its read-only view; read from the list so a save shows the new state.
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [attention, setAttention] = useState<Attention[]>([]);
  const [watch, setWatch] = useState<WatchEntry[]>([]);
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  // Hosting as the catalogue names it; words() only for a value the catalogue does not know.
  const hostingLabel = (h: string) => catalogue?.hostingLabels?.[h] ?? words(h);
  const [editing, setEditing] = useState<{ system: AiSystem | null } | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [lookups, setLookups] = useState<AiLookups | null>(null);
  // The organisation's RoPA entries and DPIAs, served with the register (they are in editions
  // that do not carry the data protection pages, so /gdpr is not read for them).
  const [dpLinks, setDpLinks] = useState<{ ropaEntries: Array<{ id: string; name: string }>; dpias: Array<{ id: string; title: string }> } | null>(null);
  const [view, setView] = useState<AiView>("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("risk");
  const tagData = useEntityTagData();
  const [tagFilter, setTagFilter] = useTagFilter();
  // ?system=<id>: a cross-reference elsewhere linked straight to one system.
  const [searchParams, setSearchParams] = useSearchParams();
  const systemParam = searchParams.get("system");

  const load = useCallback(async () => {
    try {
      const [reg, w, cat] = await Promise.all([
        api.get<{ systems: AiSystem[]; attention: Attention[]; links?: { ropaEntries: Array<{ id: string; name: string }>; dpias: Array<{ id: string; title: string }> } }>("/ai-systems"),
        api.get<{ entries: WatchEntry[] }>("/ai-systems/watch").catch(() => ({ entries: [] })),
        api.get<Catalogue>("/ai-systems/catalogue"),
      ]);
      setSystems(reg.systems);
      setAttention(reg.attention);
      setDpLinks(reg.links ?? null);
      setWatch(w.entries);
      setCatalogue(cat);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load the AI register."));
      setSystems([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  /**
   * After an impact assessment changes, refresh the register (tiles, attention, the view's
   * summary) without re-reading the catalogue, so the open panel keeps its state.
   */
  const refreshKeepingOpen = useCallback(async () => {
    try {
      const reg = await api.get<{ systems: AiSystem[]; attention: Attention[] }>("/ai-systems");
      setSystems(reg.systems);
      setAttention(reg.attention);
    } catch { /* the panel has already shown its own result */ }
  }, []);
  useEffect(() => {
    if (!systemParam || !systems) return;
    const hit = systems.find((s) => s.id === systemParam);
    if (hit) show(hit);
    const next = new URLSearchParams(searchParams); next.delete("system"); setSearchParams(next, { replace: true });
  }, [systemParam, systems]); // eslint-disable-line react-hooks/exhaustive-deps
  // The link pickers' options (and the linked records' names in the view), read once when a
  // system is first opened.
  useEffect(() => {
    if (!(editing || viewingId) || lookups) return;
    (async () => {
      const [suppliers, assets, risks] = await Promise.all([
        api.get<{ suppliers: Array<{ id: string; name: string }> }>("/tprm/suppliers").then((d) => d.suppliers.map((s) => ({ id: s.id, name: s.name }))).catch(() => []),
        api.get<{ assets: Array<{ id: string; name: string }> }>("/resilience/assets").then((d) => d.assets.map((a) => ({ id: a.id, name: a.name }))).catch(() => []),
        api.get<{ risks: Array<{ id: string; displayId: number | null; title: string }> }>("/risks").then((d) => d.risks.map((r) => ({ id: r.id, name: riskRef(r) }))).catch(() => []),
      ]);
      setLookups({
        dpias: (dpLinks?.dpias ?? []).map((d) => ({ id: d.id, name: d.title })),
        ropa: dpLinks?.ropaEntries ?? [],
        suppliers, assets, risks,
      });
    })();
  }, [editing, viewingId, lookups, dpLinks]);

  const shown = useMemo(() => {
    if (!systems) return [];
    const rows = systems.filter((s) =>
      inAiView(s, view)
      && matchesEntityTagFilter(tagData, entityRef({ kind: "ai_system", id: s.id }), tagFilter)
      && matchesSearch(search, s.name, s.vendor, s.purpose, s.modelName ?? null, s.ownerName));
    const cmp = AI_SORTS.find((o) => o.key === sort)?.compare;
    return cmp ? [...rows].sort(cmp) : rows;
  }, [systems, view, tagData, tagFilter, search, sort]);

  function exportCsv() {
    const columns: ExportColumn<AiSystem>[] = [
      { header: "Name", value: (s) => s.name },
      { header: "Vendor", value: (s) => s.vendor },
      { header: "Purpose", value: (s) => s.purpose },
      { header: "Risk class", value: (s) => RISK[s.riskClass]?.label ?? s.riskClass },
      { header: "Lifecycle", value: (s) => words(s.lifecycleStage) },
      { header: "Your role", value: (s) => (s.role ? words(s.role) : null) },
      { header: "Hosting", value: (s) => (s.hosting ? hostingLabel(s.hosting) : null) },
      { header: "Owner", value: (s) => s.ownerName },
      { header: "Confirmed", value: (s) => s.confirmedAt?.slice(0, 10) ?? (s.detectedFrom ? "Detected, not confirmed" : null) },
      { header: "Detected in", value: (s) => detectedFromLabel(s.detectedFrom) },
      { header: "Last reviewed", value: (s) => s.reviewedAt?.slice(0, 10) },
      { header: "Impact assessment residual", value: (s) => s.impactAssessment?.current?.residualRating ?? null },
      { header: "Impact assessment approved", value: (s) => s.impactAssessment?.current?.approvedAt?.slice(0, 10) },
      { header: "Impact assessment review due", value: (s) => s.impactAssessment?.current?.reviewDueAt?.slice(0, 10) },
    ];
    downloadCsv("ai-systems", columns, shown);
  }

  async function markReviewed(system: AiSystem) {
    setSaving(true); setError("");
    try {
      await api.post(`/ai-systems/${system.id}/review`, {});
      setNotice(`${system.name} recorded as reviewed today.`);
      await load();
    } catch (e) { setError(apiErrorMessage(e, "Could not record the review.")); }
    finally { setSaving(false); }
  }

  // Only an entry someone added can be deleted: a detected one would come back on the next scan
  // (the server refuses it), so it is retired or marked not permitted instead.
  async function remove(system: AiSystem) {
    if (!window.confirm(`Delete ${system.name} from the AI register? Its impact assessments go with it.`)) return;
    setSaving(true); setError("");
    try {
      await api.del(`/ai-systems/${system.id}`);
      setViewingId(null);
      setNotice(`${system.name} deleted from the register.`);
      await load();
    } catch (e) { setError(apiErrorMessage(e, "Could not delete the entry.")); }
    finally { setSaving(false); }
  }

  async function scan() {
    setScanning(true); setError(""); setNotice("");
    try {
      const { scan: result } = await api.post<{ scan: { matched: number; created: number; updated: number } }>("/ai-systems/scan", {});
      setNotice(result.matched === 0
        ? "No AI tools matched in the installed-software inventory. Browser-based tools authorised through OAuth are not visible yet — see the note in the register."
        : `${result.matched} tool(s) matched: ${result.created} new, ${result.updated} refreshed.`);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, "The scan did not complete."));
    } finally {
      setScanning(false);
    }
  }

  function formOf(system: AiSystem): Form {
    return {
      name: system.name, purpose: system.purpose ?? "", vendor: system.vendor ?? "",
      hosting: system.hosting ?? "vendor_saas", lifecycleStage: system.lifecycleStage,
      role: system.role ?? "deployer", ownerName: system.ownerName ?? "",
      annexIIIPurposes: system.annexIIIPurposes,
      interactsWithPeople: system.interactsWithPeople, generatesContent: system.generatesContent,
      emotionRecognition: system.emotionRecognition, prohibitedPractice: system.prohibitedPractice,
      classified: !!system.riskAssessedAt,
      notes: system.notes ?? "",
      modelDevelopmentAssessment: system.modelDevelopmentAssessment ?? "",
      impactAssessmentId: system.impactAssessmentId ?? "", supplierId: system.supplierId ?? "", assetId: system.assetId ?? "",
      ropaEntryId: system.ropaEntryId ?? "", dataCategories: system.dataCategories ?? [],
      riskIds: system.riskIds ?? [],
    };
  }

  /** Open a system's read-only view (a row or a deep link). */
  function show(system: AiSystem) {
    setViewingId(system.id);
    setError("");
    setNotice("");
  }

  /** Open the edit form (from the view's Edit) or the add form. */
  function open(system: AiSystem | null) {
    setForm(system ? formOf(system) : EMPTY);
    setError("");
    if (system) setViewingId(system.id);
    setEditing({ system });
  }

  async function save() {
    if (!form.name.trim()) { setError("A name is required."); return; }
    setSaving(true); setError("");
    const body = {
      name: form.name.trim(),
      purpose: form.purpose.trim() || null,
      vendor: form.vendor.trim() || null,
      hosting: form.hosting || null,
      lifecycleStage: form.lifecycleStage,
      role: form.role || null,
      ownerName: form.ownerName.trim() || null,
      // Sent only once answered: a form that always sent "no" made every new or confirmed system Minimal (RES-21).
      ...(form.classified ? {
        annexIIIPurposes: form.annexIIIPurposes,
        interactsWithPeople: form.interactsWithPeople,
        generatesContent: form.generatesContent,
        emotionRecognition: form.emotionRecognition,
        prohibitedPractice: form.prohibitedPractice,
      } : {}),
      notes: form.notes.trim() || null,
      modelDevelopmentAssessment: form.modelDevelopmentAssessment.trim() || null,
      impactAssessmentId: form.impactAssessmentId || null,
      supplierId: form.supplierId || null,
      assetId: form.assetId || null,
      ropaEntryId: form.ropaEntryId || null,
      dataCategories: form.dataCategories,
      riskIds: form.riskIds,
    };
    try {
      // Back to the record's view, not the list, so the saved state is what is shown next.
      if (editing?.system) await api.put(`/ai-systems/${editing.system.id}`, body);
      else {
        const created = await api.post<{ system?: { id: string } }>("/ai-systems", body);
        setViewingId(created?.system?.id ?? null);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save the system."));
    } finally {
      setSaving(false);
    }
  }

  async function acknowledge(key: string) {
    try {
      await api.post(`/ai-systems/watch/${encodeURIComponent(key)}/acknowledge`, {});
      await load();
    } catch (e) { setError(apiErrorMessage(e, "Could not record the acknowledgement.")); }
  }

  // My Actions links a regulatory watch entry here by its key.
  useDeepLinkScroll(searchParams.get("watch"), systems !== null && !!catalogue && watch.some((w) => !w.acknowledged));

  if (systems === null || !catalogue) return <LoadingBlock />;

  const unacknowledged = watch.filter((w) => !w.acknowledged);
  const liveSystems = systems.filter(live);
  const classCount = (c: string) => liveSystems.filter((s) => s.riskClass === c).length;
  const tile = (key: AiView) => ({ onClick: () => setView(view === key ? "all" : key), active: view === key });
  const impactCatalogue: ImpactCatalogue | null = catalogue.impactDimensions && catalogue.likelihoodLabels && catalogue.severityLabels
    ? { impactDimensions: catalogue.impactDimensions, likelihoodLabels: catalogue.likelihoodLabels, severityLabels: catalogue.severityLabels, impactReviewMonths: catalogue.impactReviewMonths }
    : null;
  const viewing = !editing && viewingId ? systems.find((s) => s.id === viewingId) ?? null : null;
  const modalOpen = !!editing || !!viewing;

  return (
    <div className="page-pad">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={pageHeading}>AI systems</h1>
          <p style={{ ...pageSubtitle, maxWidth: 660 }}>
            The inventory ISO 42001 asks for, and what the EU AI Act makes of each entry. The risk
            class is worked out from your answers rather than chosen — the same model is minimal risk
            summarising notes and high risk screening job applicants.
          </p>
          <PlaybookBar ids={["ai-governance"]} />
        </div>
        {canEdit && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={scan} disabled={scanning} style={{ ...secondaryBtn, display: "flex", alignItems: "center", gap: 6 }}>
              <Radar size={15} /> {scanning ? "Scanning…" : "Scan for shadow AI"}
            </button>
            <button onClick={() => { setViewingId(null); open(null); }} style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }}>
              <Plus size={15} /> Add a system
            </button>
          </div>
        )}
      </div>

      {error && !modalOpen && <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${theme.color.danger}`, color: theme.color.danger, fontSize: text.body.fontSize }}>{error}</div>}
      {notice && !modalOpen && <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${theme.color.accent}`, ...text.body, color: theme.color.subtle }}>{notice}</div>}

      {attention.length > 0 && (
        <div style={{ ...card, marginBottom: 20, borderLeft: `3px solid ${theme.color.warning}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
            <ShieldAlert size={15} color={theme.color.warning} />
            <span style={{ ...text.sectionTitle, margin: 0 }}>Needs attention</span>
          </div>
          {attention.map((a) => (
            <div key={a.id} style={{ ...text.hint, marginBottom: 6 }}>
              <strong style={{ color: theme.color.text }}>{a.title}</strong> — {a.description}
            </div>
          ))}
        </div>
      )}

      {unacknowledged.length > 0 && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ ...text.sectionTitle, marginBottom: 4 }}>Standards and laws that have moved</div>
          <div style={{ ...text.hint, marginBottom: 10 }}>
            Only the ones affecting standards you are enrolled in and laws in your operating locations.
          </div>
          {unacknowledged.map((w) => (
            <div key={w.key} data-deeplink={w.key} style={{ borderTop: `1px solid ${theme.color.border}`, paddingTop: 10, marginTop: 10 }}>
              <div style={text.itemTitle}>
                {w.currentEdition} → {w.newEdition}
              </div>
              <div style={{ ...text.hint, margin: "4px 0 6px" }}>{w.summary}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ ...text.meta, color: w.platformUpdated ? theme.color.success : theme.color.warning, fontWeight: 600 }}>
                  {w.origin === "standards_watch"
                    ? "The platform now assesses against the new text — review what changed"
                    : w.platformUpdated ? "The platform already reflects this" : "The platform has not caught up — treat as a manual check"}
                </span>
                <span style={{ ...text.meta, color: theme.color.subtle }}>{fmtDateOnly(w.publishedOn)}</span>
                {safeHref(w.url) && <a href={safeHref(w.url)} target="_blank" rel="noreferrer" style={{ ...text.meta, color: theme.color.accent }}>Source</a>}
                {canEdit && <button onClick={() => acknowledge(w.key)} style={{ ...secondaryBtn, padding: "3px 10px", fontSize: text.meta.fontSize }}>Acknowledge</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <SummaryTiles tiles={[
        { key: "prohibited", label: "Prohibited", value: classCount("prohibited"), tone: classCount("prohibited") ? "danger" : "neutral", ...tile("prohibited") },
        { key: "high", label: "High risk", value: classCount("high"), tone: classCount("high") ? "warning" : "neutral", ...tile("high") },
        { key: "limited", label: "Limited", value: classCount("limited"), ...tile("limited") },
        { key: "minimal", label: "Minimal", value: classCount("minimal"), ...tile("minimal") },
        { key: "unassessed", label: "Unassessed", value: classCount("unassessed"), tone: classCount("unassessed") ? "warning" : "neutral", hint: "Not the same as minimal", ...tile("unassessed") },
        { key: "unconfirmed", label: "Detected, unconfirmed", value: systems.filter(unconfirmed).length, tone: systems.some(unconfirmed) ? "warning" : "neutral", ...tile("unconfirmed") },
        { key: "assessments", label: "Impact assessments due", value: systems.filter(assessmentDue).length, tone: systems.some(assessmentDue) ? "danger" : "neutral", hint: `Missing for high risk, or review within ${ASSESSMENT_DUE_SOON_DAYS} days`, ...tile("assessments") },
      ]} />

      <RegisterToolbar<AiSystem>
        search={search} onSearch={setSearch} placeholder="Search name, vendor, purpose"
        sortOptions={AI_SORTS} sort={sort} onSort={setSort}
        onExport={exportCsv}
        count={{ shown: shown.length, total: systems.length }}
      >
        {view !== "all" && (
          <button type="button" onClick={() => setView("all")} style={{ ...secondaryBtn, padding: "5px 10px", fontSize: text.hint.fontSize }}>
            Clear filter
          </button>
        )}
        <EntityTagFilters value={tagFilter} onChange={setTagFilter} />
      </RegisterToolbar>

      {shown.length === 0 ? (
        <EmptyState
          title={systems.length ? "No AI systems match" : "No AI systems recorded"}
          detail={systems.length ? "Clear the search or pick another tile to see more." : "Add the ones you know about, or scan the installed-software inventory for tools already in use."}
        />
      ) : (
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          {shown.map((s, i) => (
            <div key={s.id} style={{ borderTop: i === 0 ? "none" : `1px solid ${theme.color.border}` }}>
            <button
              onClick={() => show(s)}
              style={{
                display: "block", width: "100%", textAlign: "left", background: "transparent",
                border: "none", padding: "12px 16px 4px", cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4,
                  background: RISK[s.riskClass]?.bg ?? theme.color.panelAlt, color: RISK[s.riskClass]?.color ?? theme.color.subtle,
                }}>
                  {RISK[s.riskClass]?.label ?? s.riskClass}
                </span>
                <span style={{ ...text.itemTitle, flex: 1 }}>{s.name}</span>
                {s.impactAssessment?.current && (
                  <span style={{ ...text.meta, color: theme.color.subtle, display: "inline-flex", gap: 4, alignItems: "center" }}>
                    Impact <RatingBadge rating={s.impactAssessment.current.residualRating} />
                  </span>
                )}
                {assessmentDue(s) && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: theme.color.dangerBg, color: theme.color.dangerText }}>
                    {s.impactAssessment?.current ? "Assessment review due" : "No impact assessment"}
                  </span>
                )}
                {!s.confirmedAt && s.detectedFrom && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: theme.color.warningBg, color: theme.color.warningText }}>
                    Detected
                  </span>
                )}
              </div>
              <div style={{ ...text.hint, marginTop: 4 }}>
                {s.vendor && <>{s.vendor} · </>}
                {words(s.lifecycleStage)}
                {s.role && <> · {words(s.role)}</>}
                {s.detectedFrom && <> · found in {detectedFromLabel(s.detectedFrom)}</>}
                {s.reviewedAt && <> · reviewed {fmtDay(s.reviewedAt)}</>}
              </div>
            </button>
            <div style={{ padding: "0 16px 10px" }}>
              <EntityTagsRow entity={{ kind: "ai_system", id: s.id }} readOnly style={{ marginBottom: 0 }} />
            </div>
            </div>
          ))}
        </div>
      )}

      {viewing && (() => {
        const v = viewing;
        const close = () => { setViewingId(null); setError(""); };
        const yesNo = (b: boolean) => (b ? "Yes" : "No");
        const purposes = v.annexIIIPurposes.map((id) => catalogue.annexIIIPurposes.find((p) => p.id === id)?.label ?? id);
        return (
          <ModalOverlay onClose={close} wide>
            <ModalHeader title={v.name} onClose={close} right={canEdit ? <EditButton onClick={() => open(v)} /> : undefined} />
            <div style={{ padding: 20 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: RISK[v.riskClass]?.bg ?? theme.color.panelAlt, color: RISK[v.riskClass]?.color ?? theme.color.subtle }}>
                  {RISK[v.riskClass]?.label ?? v.riskClass}
                </span>
                {unconfirmed(v) && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: theme.color.warningBg, color: theme.color.warningText }}>Detected</span>
                )}
                {assessmentDue(v) && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: theme.color.dangerBg, color: theme.color.dangerText }}>
                    {v.impactAssessment?.current ? "Assessment review due" : "No impact assessment"}
                  </span>
                )}
              </div>

              <NextSteps items={systemSteps(v, formOf(v))} />

              {unconfirmed(v) && (
                <div style={{ ...text.hint, marginBottom: 12, padding: "8px 10px", background: theme.color.panelAlt, borderRadius: 3 }}>
                  Detected in {detectedFromLabel(v.detectedFrom)}: {v.detectionEvidence}. {canEdit ? "Editing and saving the entry confirms it." : "Not yet confirmed as in use."}
                </div>
              )}

              {canEdit && onboardable(v) && (
                <DetailSection title="Onboard this tool">
                  <ShadowAiOnboardingPanel
                    systemId={v.id}
                    systemName={v.name}
                    onDone={(message) => { setNotice(message); setLookups(null); void refreshKeepingOpen(); }}
                  />
                </DetailSection>
              )}

              {dpiaCheckOpen(v) && user?.permissions?.includes("privacy:write") && canEdit && (
                <DetailSection title="DPIA check">
                  <DpiaCheckOutcome systemId={v.id} reasons={v.dpiaCheckReasons ?? []} onDone={(message) => { setNotice(message); void refreshKeepingOpen(); }} />
                </DetailSection>
              )}

              <DetailSection title="Details">
                <DetailFields fields={[
                  { label: "What it is used for", value: v.purpose, wide: true, multiline: true },
                  { label: "Vendor", value: v.vendor },
                  { label: "Model", value: v.modelName ?? null },
                  { label: "Lifecycle stage", value: words(v.lifecycleStage) },
                  { label: "Hosting", value: v.hosting ? hostingLabel(v.hosting) : null },
                  { label: "Your role", value: v.role ? words(v.role) : null },
                  { label: "Owner", value: v.ownerName },
                  { label: "Confirmed", value: v.confirmedAt ? fmtDay(v.confirmedAt) : v.detectedFrom ? "Detected, not confirmed" : null },
                  { label: "Detected in", value: detectedFromLabel(v.detectedFrom) },
                  ...(v.onboardedAt ? [{ label: "Onboarded", value: fmtDay(v.onboardedAt) }] : []),
                  ...(v.dpiaCheckResolvedAt ? [{ label: "DPIA check", value: `${v.dpiaCheckResolution ?? "Outcome recorded"} (${fmtDay(v.dpiaCheckResolvedAt)})`, wide: true, multiline: true }] : []),
                  { label: "Last reviewed", value: v.reviewedAt ? fmtDay(v.reviewedAt) : null },
                  { label: "Personal data it handles", value: v.dataCategories?.length ? v.dataCategories.map(dataCategoryLabel).join(", ") : null },
                  { label: "Processing record", value: v.ropaEntry?.name ?? null },
                  ...(v.modelDevelopmentAssessment ? [{ label: "How the model was developed", value: `${v.modelDevelopmentAssessment}${v.modelDevelopmentAssessedAt ? ` (recorded ${fmtDay(v.modelDevelopmentAssessedAt)})` : ""}`, wide: true, multiline: true }] : []),
                  { label: "Notes", value: v.notes, wide: true, multiline: true },
                ]} />
              </DetailSection>

              <DetailSection title="EU AI Act classification">
                <div style={{ ...text.hint, marginBottom: 10 }}>
                  The class is worked out from these answers. Unanswered is recorded as unassessed, which is not the same as minimal risk.
                </div>
                <DetailFields fields={[
                  { label: "Annex III purposes", value: purposes.length ? purposes.join("; ") : "None", wide: true },
                  { label: "Interacts directly with people", value: yesNo(v.interactsWithPeople) },
                  { label: "Generates or manipulates content", value: yesNo(v.generatesContent) },
                  { label: "Recognises emotion or categorises biometrically", value: yesNo(v.emotionRecognition) },
                  { label: "Involves a practice Art. 5 prohibits", value: yesNo(v.prohibitedPractice) },
                ]} />
                {v.obligations.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ ...text.label, marginBottom: 4 }}>What follows ({RISK[v.riskClass]?.label ?? v.riskClass})</div>
                    <ul style={{ ...text.body, margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                      {v.obligations.map((o, i) => <li key={i}>{o}</li>)}
                    </ul>
                  </div>
                )}
              </DetailSection>

              {impactCatalogue && (
                <DetailSection title="AI impact assessment">
                  <AiImpactAssessmentPanel
                    systemId={v.id}
                    riskClass={v.riskClass}
                    catalogue={impactCatalogue}
                    canEdit={canEdit}
                    onChanged={refreshKeepingOpen}
                    dpias={(dpLinks?.dpias ?? []).map((d) => ({ id: d.id, name: d.title }))}
                  />
                </DetailSection>
              )}

              <DetailSection title="Linked records">
                <EntityTagsRow entity={{ kind: "ai_system", id: v.id }} />
                <RelatedRecords entity={{ kind: "ai_system", id: v.id }} links={systemLinks(formOf(v), lookups)} />
              </DetailSection>

              {notice && <div style={{ ...text.hint, color: theme.color.accentText, margin: "4px 0 10px" }}>{notice}</div>}
              {error && <div style={{ ...text.hint, color: theme.color.danger, margin: "4px 0 10px" }}>{error}</div>}

              {canEdit && (
                <RecordActions>
                  <button onClick={() => markReviewed(v)} disabled={saving} style={{ ...secondaryBtn, opacity: saving ? 0.6 : 1 }} title="Record that the entry has been checked today (ISO 42001 planned review)">
                    Mark reviewed
                  </button>
                  {!v.detectionKey && !v.impactAssessment?.current && (
                    <button onClick={() => remove(v)} disabled={saving} style={{ ...dangerBtn, opacity: saving ? 0.6 : 1 }}>Delete</button>
                  )}
                </RecordActions>
              )}
            </div>
          </ModalOverlay>
        );
      })()}

      {editing && (
        <ModalOverlay onClose={() => setEditing(null)} wide>
          <ModalHeader title={editing.system ? `Edit ${editing.system.name}` : "Add an AI system"} onClose={() => setEditing(null)} />
          <div style={{ padding: 20 }}>

            {editing.system && <NextSteps items={systemSteps(editing.system, form)} />}

            {editing.system?.detectedFrom && !editing.system.confirmedAt && (
              <div style={{ ...text.hint, marginBottom: 12, padding: "8px 10px", background: "var(--c-panelAlt)", borderRadius: 3 }}>
                Detected in {editing.system.detectedFrom}: {editing.system.detectionEvidence}. Saving confirms it.
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
              <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={input} /></Field>
              <Field label="Vendor"><input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} style={input} /></Field>
              <Field label="What it is used for" span2>
                <textarea value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} style={textarea} />
              </Field>
              <Field label="Lifecycle stage">
                <select value={form.lifecycleStage} onChange={(e) => setForm({ ...form, lifecycleStage: e.target.value })} style={input}>
                  {catalogue.lifecycleStages.map((s) => <option key={s} value={s}>{words(s)}</option>)}
                </select>
              </Field>
              <Field label="Hosting">
                <select value={form.hosting} onChange={(e) => setForm({ ...form, hosting: e.target.value })} style={input}>
                  {catalogue.hosting.map((h) => <option key={h} value={h}>{hostingLabel(h)}</option>)}
                </select>
              </Field>
              <Field label="Your role" help="A deployer has far fewer duties than a provider, and the two are constantly conflated." span2>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} style={input}>
                  {catalogue.roles.map((r) => <option key={r} value={r}>{words(r)}</option>)}
                </select>
              </Field>
              <Field label="Owner"><input value={form.ownerName} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} style={input} /></Field>
              <div />
              <Field label="Personal data it handles" help="Compared with the processing record it is linked to." span2>
                <DataCategoryPicker selected={form.dataCategories} onChange={(dataCategories) => setForm({ ...form, dataCategories })} />
              </Field>
              {([
                ["ropaEntryId", "Processing record (RoPA entry)", lookups?.ropa],
                ["impactAssessmentId", "Impact assessment (DPIA)", lookups?.dpias],
                ["supplierId", "Vendor in the supplier register", lookups?.suppliers],
                ["assetId", "Asset it runs on or supports", lookups?.assets],
              ] as Array<["ropaEntryId" | "impactAssessmentId" | "supplierId" | "assetId", string, LinkOption[] | undefined]>).map(([key, label, options]) => (
                <Field key={key} label={label}>
                  <select value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} style={input} disabled={!options}>
                    <option value="">{options ? "None" : "Loading…"}</option>
                    {form[key] && !options?.some((o) => o.id === form[key]) && <option value={form[key]}>Current link</option>}
                    {(options ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </select>
                </Field>
              ))}
              {form.role === "deployer" && (
                <Field label="How the model was developed" help="As its deployer: the sources of its training data, whether personal data was used and on what basis, and anything a supervisory authority has found about it (EDPB Opinion 28/2024). Ask the vendor where you cannot see it yourself." span2>
                  <textarea rows={3} value={form.modelDevelopmentAssessment} onChange={(e) => setForm({ ...form, modelDevelopmentAssessment: e.target.value })} style={{ ...input, resize: "vertical" }} />
                </Field>
              )}
              <Field label="Risks in the risk register" help="Hold Ctrl (Cmd on a Mac) to choose more than one. A high-risk system needs at least one." span2>
                <select multiple size={5} value={form.riskIds} disabled={!lookups}
                  onChange={(e) => setForm({ ...form, riskIds: Array.from(e.target.selectedOptions).map((o) => o.value) })} style={{ ...input, height: "auto" }}>
                  {(lookups?.risks ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </Field>
            </div>

            <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${theme.color.border}` }}>
              <div style={{ ...text.sectionTitle, marginBottom: 2 }}>EU AI Act classification</div>
              <div style={{ ...text.hint, marginBottom: 10 }}>
                The class is worked out from these answers. Leaving them unanswered is recorded as
                unassessed, which is not the same as minimal risk.
              </div>
              <div style={{ ...text.label, marginBottom: 6 }}>Annex III purposes — any one makes it high risk</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", marginBottom: 12 }}>
                {catalogue.annexIIIPurposes.map((p) => (
                  <label key={p.id} style={{ ...text.hint, color: theme.color.text, display: "flex", gap: 6, alignItems: "flex-start", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={form.annexIIIPurposes.includes(p.id)}
                      onChange={() => setForm({
                        ...form,
                        classified: true,
                        annexIIIPurposes: form.annexIIIPurposes.includes(p.id)
                          ? form.annexIIIPurposes.filter((x) => x !== p.id)
                          : [...form.annexIIIPurposes, p.id],
                      })}
                      style={{ marginTop: 2 }}
                    />
                    <span>{p.label}</span>
                  </label>
                ))}
              </div>
              {([
                ["interactsWithPeople", "Interacts directly with people"],
                ["generatesContent", "Generates or manipulates content"],
                ["emotionRecognition", "Recognises emotion or categorises biometrically"],
                ["prohibitedPractice", "Involves a practice Art. 5 prohibits (social scoring, untargeted face-scraping, manipulation)"],
              ] as const).map(([key, label]) => (
                <label key={key} style={{ ...text.hint, color: theme.color.text, display: "flex", gap: 6, alignItems: "flex-start", cursor: "pointer", marginBottom: 4 }}>
                  <input type="checkbox" checked={form[key]} onChange={() => setForm({ ...form, classified: true, [key]: !form[key] })} style={{ marginTop: 2 }} />
                  <span>{label}</span>
                </label>
              ))}
              {!(editing?.system?.riskAssessedAt) && (
                <label style={{ ...text.hint, color: theme.color.text, display: "flex", gap: 6, alignItems: "flex-start", cursor: "pointer", marginTop: 8 }}>
                  <input type="checkbox" checked={form.classified} onChange={() => setForm({ ...form, classified: !form.classified })} style={{ marginTop: 2 }} />
                  <span>I have answered these questions{form.classified && !form.annexIIIPurposes.length && !form.interactsWithPeople && !form.generatesContent && !form.emotionRecognition && !form.prohibitedPractice ? " (none of them applies, so it is minimal risk)" : ""}. Leave this unticked to record the class as unassessed.</span>
                </label>
              )}
            </div>

            {error && <div style={{ ...text.hint, color: theme.color.danger, marginTop: 10 }}>{error}</div>}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16, flexWrap: "wrap" }}>
              <button onClick={() => setEditing(null)} style={secondaryBtn}>Cancel</button>
              <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}
